/**
 * App Component - Root application component
 *
 * Handles routing, authentication flow, and view rendering.
 * Now fully integrated with SSOT Context layer.
 *
 * @version 5.4.0 - TypeScript Migration
 */

import { useState, useEffect, useCallback, memo } from 'react'
import { useAuth } from '@/context'
import '@/App.css'

// Shared Components
import Navigation from '@/components/shared/Navigation'
import Login from '@/components/shared/Login'
import PasswordChangeModal from '@/components/shared/PasswordChangeModal'
// Dashboard Components
import SmartDashboard from '@/components/dashboard/SmartDashboard'

// Core Page Components (standalone nav items)
import MultiTimeframeTrader from '@/components/signals/MultiTimeframeTrader'

// Hub Components (V2 Architecture - tabbed pages)
import {
  AIIntelligenceHub,
  DataInfrastructureHub,
  PortfolioHub,
  HelpHub,
  SettingsHub,
  TradingValidationHub,
} from '@/components/hubs'

// Type definitions - V2 Navigation (8 core + 1 admin)
type ViewType =
  | 'dashboard'
  | 'trade-analysis'
  | 'ai-intelligence'
  | 'portfolio'
  | 'trading-validation'
  | 'settings'
  | 'help'
  | 'data-infrastructure'
  | string // backward compat for old view IDs

type SearchMethod = 'text' | 'voice' | 'nlp'

interface LoginCredentials {
  email: string
  password: string
}

// Memoized Loading Component
const LoadingScreen = memo(function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400">Loading...</span>
      </div>
    </div>
  )
})

function App(): JSX.Element {
  // View state
  const [currentView, setCurrentView] = useState<ViewType>('dashboard')
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(true)
  const [showPasswordChange, setShowPasswordChange] = useState<boolean>(false)

  // Search state
  const [searchQuery, setSearchQuery] = useState<string | null>(null)
  const [searchMethod, setSearchMethod] = useState<SearchMethod>('text')

  // Auth from Context (SSOT - Single Source of Truth)
  const {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    changePassword
  } = useAuth()

  // Handle login success from Login component
  const handleLoginSuccess = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    if (credentials?.email && credentials?.password) {
      await login(credentials.email, credentials.password)
    }
  }, [login])

  // Handle password change success
  const handlePasswordChangeSuccess = useCallback(async (): Promise<void> => {
    setShowPasswordChange(false)
  }, [])

  // Handle logout
  const handleLogout = useCallback(async (): Promise<void> => {
    await logout()
    setCurrentView('dashboard')
  }, [logout])

  // Toggle menu
  const handleMenuToggle = useCallback((): void => {
    setIsMenuOpen(prev => !prev)
  }, [])

  // Search handler function
  const handleSearch = useCallback((query: string, method: SearchMethod = 'text'): void => {
    setSearchQuery(query)
    setSearchMethod(method)
    if (!currentView.startsWith('dashboard') && !currentView.startsWith('markets')) {
      setCurrentView('dashboard')
    }
  }, [currentView])

  // Check if first login requires password change
  useEffect(() => {
    if (user?.isFirstLogin) {
      setShowPasswordChange(true)
    }
  }, [user])

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  const renderView = (): JSX.Element | null => {
    // Dashboard handled in JSX below
    if (currentView === 'dashboard' || currentView.startsWith('markets')) {
      return null
    }

    switch (currentView) {
      // ── Core Views (7 nav items) ──
      case 'trade-analysis':
        return <MultiTimeframeTrader theme="dark" />

      case 'ai-intelligence':
        return <AIIntelligenceHub />

      case 'portfolio':
        return <PortfolioHub />

      case 'trading-validation':
        return <TradingValidationHub />

      case 'strategies':
        return <AIIntelligenceHub />

      case 'settings':
        return <SettingsHub />

      case 'help':
        return <HelpHub />

      // ── Admin Views ──
      case 'data-infrastructure':
        return <DataInfrastructureHub />

      // ── Backward Compatibility ──
      // Old AI views → AI Intelligence Hub
      case 'ai-signals':
      case 'ai-signals-predictions':
      case 'ai-signals-patterns':
      case 'ai-signals-sentiment':
      case 'ai-signals-signals':
      case 'ai-signals-anomalies':
      case 'ai-chat':
      case 'opportunity-report':
      case 'ai-trading-lab':
      case 'ml-test-data':
      case 'walk-forward':
      case 'ai-model-training':
      case 'ml-operations':
        return <AIIntelligenceHub />

      // Old alert routes → Portfolio Hub (alerts merged into portfolio)
      case 'alerts':
      case 'alert-builder':
        return <PortfolioHub />

      // Old education routes → Help Hub (education merged into help)
      case 'education':
      case 'ai-training':
      case 'documents':
      case 'ai-framework':
        return <HelpHub />

      // Old data routes → Data Infrastructure Hub
      case 'data-export':
      case 'pipeline-monitor':
      case 'table-inventory':
      case 'database-summary':
      case 'data-warehouse-status':
      case 'data-infrastructure-inventory':
      case 'data-infrastructure-warehouse':
      case 'data-infrastructure-summary':
      case 'data-infrastructure-export':
        return <DataInfrastructureHub />

      // Old analytics / weekly → Dashboard
      case 'weekly':
      case 'market-movers':
      case 'fundamentals':
      case 'etf-analytics':
      case 'charts':
        return <SmartDashboard searchQuery={null} searchMethod="text" />

      // Old admin/analysis routes
      case 'admin':
        return <SettingsHub />
      case 'analysis':
        return <AIIntelligenceHub />

      // Default fallback
      default:
        return <SmartDashboard searchQuery={null} searchMethod="text" />
    }
  }

  return (
    <div className="app bg-slate-900 min-h-screen">
      <Navigation
        currentView={currentView}
        onViewChange={setCurrentView}
        isMenuOpen={isMenuOpen}
        onMenuToggle={handleMenuToggle}
        currentUser={user}
        onSearch={handleSearch}
        onLogout={handleLogout}
      />
      <main
        className="transition-all duration-300 ease-in-out"
        style={{
          marginLeft: isMenuOpen ? '280px' : '0',
          marginTop: '64px'
        }}
      >
        {currentView === 'dashboard' || currentView.startsWith('markets') ? (
          <SmartDashboard searchQuery={searchQuery} searchMethod={searchMethod} />
        ) : (
          renderView()
        )}
      </main>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <PasswordChangeModal
          onClose={() => setShowPasswordChange(false)}
          onSuccess={handlePasswordChangeSuccess}
          onChangePassword={changePassword}
          isFirstLogin={user?.isFirstLogin}
        />
      )}
    </div>
  )
}

export default App
